import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  Link,
} from '@mui/material';
import { requestApi } from '../../api/request_api';
import type { RequestCreditItem } from '../../api/request_api';
import { StatusChip } from '../../components/StatusChip';

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/** /settings/credits: the client's tasks and the credits each one used. */
export const SettingsCredits = () => {
  const [items, setItems] = useState<RequestCreditItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [tasksRemaining, setTasksRemaining] = useState<number | null>(null);
  const [tasksExpire, setTasksExpire] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async (from: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await requestApi.listCredits(from ?? undefined);
      setItems((prev) => (from ? [...prev, ...response.items] : response.items));
      setCursor(response.cursor);
      setHasMore(response.has_more && !!response.cursor);
      if (!from) {
        setTasksRemaining(response.tasks_remaining ?? 0);
        setTasksExpire(response.tasks_expire ?? null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch credits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(null);
  }, [fetchPage]);

  if (loading && items.length === 0 && tasksRemaining === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Tasks Remaining
            </Typography>
            <Typography variant="h5">{tasksRemaining ?? '-'}</Typography>
          </Box>
          {tasksExpire && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Expires
              </Typography>
              <Typography variant="h5">{formatDate(tasksExpire)}</Typography>
            </Box>
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          Credits Used per Task
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {items.length === 0 ? (
          <Typography color="text.secondary">No tasks yet.</Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Task</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Credits</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.key}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(item.date_created)}
                      </TableCell>
                      <TableCell>
                        <Link component={RouterLink} to={`/request/${item.key}`}>
                          {item.title || 'Untitled Request'}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={item.status} variant="simple" />
                      </TableCell>
                      <TableCell align="right">{item.task_credits}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  onClick={() => fetchPage(cursor)}
                  disabled={loading}
                  variant="outlined"
                >
                  {loading ? <CircularProgress size={20} /> : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
